import React, { useEffect, useState } from 'react';
import { FiKey, FiEye, FiEyeOff } from 'react-icons/fi';
import { format } from 'date-fns';
import { aiTokenService } from '../../../services/aiTokenService';

const STATUS_BADGES = {
  valid: { bg: 'bg-green-100', text: 'text-green-800', label: 'Connected' },
  invalid: { bg: 'bg-red-100', text: 'text-red-800', label: 'Invalid credentials' },
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Not tested yet' },
};

/**
 * Shared connect/test/disconnect UI for a single-provider-at-a-time
 * credential (AI Provider or Image Provider). MidJourney has its own card
 * since its fields differ (Discord token + channel, not a plain API key).
 */
export default function ProviderCredentialCard({
  title,
  description,
  capability,
  providers,
  helperNote,
  alert,
  confirm,
}) {
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState(providers[0]?.value);
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [message, setMessage] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await aiTokenService.getConnections(capability);
    if (error) {
      setMessage({ type: 'error', text: `Couldn't load your saved connection: ${error.message}` });
    }
    setConnection(data?.[0] || null);
    if (data?.[0]?.provider) setProvider(data[0].provider);
    setLoading(false);
  };

  useEffect(() => { load(); }, [capability]);

  const handleSave = async () => {
    if (!apiKey.trim()) {
      setMessage({ type: 'error', text: 'Please enter an API key.' });
      return;
    }
    setIsSaving(true);
    setMessage(null);
    const { error } = await aiTokenService.saveProvider({ provider, capability, apiKey });
    setIsSaving(false);
    if (error) {
      setMessage({ type: 'error', text: error.message });
      return;
    }
    setApiKey('');
    setMessage({ type: 'success', text: capability === 'image' ? 'Image-generation connection saved successfully.' : 'AI provider connected successfully.' });
    await load();
  };

  const handleTest = async () => {
    setIsTesting(true);
    setMessage(null);
    const { data, error } = await aiTokenService.testProvider(capability);
    setIsTesting(false);
    if (error) {
      setMessage({ type: 'error', text: error.message });
      return;
    }
    setMessage({ type: data.status === 'valid' ? 'success' : 'error', text: data.message });
    await load();
  };

  const handleDisconnect = async () => {
    const ok = await confirm('Are you sure you want to disconnect this provider? AI generation will stop until another provider is connected.');
    if (!ok) return;
    setIsSaving(true);
    const { error } = await aiTokenService.disconnectProvider(capability);
    setIsSaving(false);
    if (error) {
      alert(error.message);
      return;
    }
    setConnection(null);
    setApiKey('');
    setMessage(null);
    await load();
  };

  const badge = STATUS_BADGES[connection?.status] || STATUS_BADGES.pending;
  const providerLabel = providers.find((p) => p.value === connection?.provider)?.label || connection?.provider;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
        <p className="text-sm text-gray-500">{description}</p>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500 text-sm">Loading…</div>
      ) : connection ? (
        <div className="border border-gray-200 rounded-xl p-5 mb-4">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <FiKey className="w-5 h-5 text-purple-600" />
              </div>
              <div className="min-w-0">
                <h4 className="font-semibold text-gray-900 capitalize mb-1">{providerLabel}</h4>
                {connection.model && <p className="text-xs text-gray-500 mb-1">Model: {connection.model}</p>}
                <code className="text-xs text-gray-700">••••••••{connection.last_four}</code>
              </div>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${badge.bg} ${badge.text}`}>{badge.label}</span>
          </div>
          <p className="text-xs text-gray-400 mb-3">
            Last tested: {connection.last_tested_at ? format(new Date(connection.last_tested_at), 'yyyy-MM-dd HH:mm') : 'Never'}
          </p>
          <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
            <button
              onClick={handleTest}
              disabled={isTesting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {isTesting ? 'Testing…' : 'Test Connection'}
            </button>
            <button
              onClick={handleDisconnect}
              disabled={isSaving}
              className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
            >
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-purple-200 rounded-xl p-8 text-center mb-4">
          <div className="flex justify-center mb-3">
            <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center">
              <FiKey className="w-7 h-7 text-purple-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500">No provider connected yet.</p>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">Provider</label>
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white"
          >
            {providers.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">API Key</label>
          <div className="relative">
            <input
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter your API key"
              className="w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            />
            <button
              type="button"
              onClick={() => setShowKey(!showKey)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showKey ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
            </button>
          </div>
          {helperNote && <p className="text-xs text-gray-400 mt-2">{helperNote}</p>}
        </div>

        <button
          onClick={handleSave}
          disabled={isSaving}
          className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-medium rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-md hover:shadow-lg disabled:opacity-50"
        >
          {isSaving ? 'Saving…' : connection ? 'Update' : 'Save'}
        </button>
      </div>

      {message && (
        <p className={`text-sm mt-3 text-center ${message.type === 'error' ? 'text-red-600' : 'text-green-600'}`}>
          {message.text}
        </p>
      )}
    </div>
  );
}
